import React, { useEffect, useState } from 'react';
import apiService from '../../services/api';
import { Box, Select, MenuItem, Snackbar, LinearProgress } from '@mui/material';
import EditSurveyDetails from '../../components/Surveys/EditSurveyDetails';
import DeleteConfirmationModal from '../../components/Surveys/DeleteConfirmationModal';
import SurveysTable from '../../components/Surveys/SurveysTable';
import AddSurveyForm from '../../components/Surveys/AddSurveyForm';
import Typography from '@mui/material/Typography';
import ColumnOptions from '../../components/Projects/ColumnOptions'; 
import './AddSurveyForm.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import AddSurveyTypeForm from '../../components/Surveys/AddSurveyTypeForm.js';
// import SendSurveyPopup from '../../components/Surveys/SendSurveyPopup'; // Removed - Send feature disabled

const editIcon = apiService.getBlobAssetUrl('Edit_small_white.svg');
const deleteteams = apiService.getBlobAssetUrl('DeleteTeams_12.svg');
const addicon = apiService.getBlobAssetUrl('Add_Icon.svg');
const search = apiService.getBlobAssetUrl('search.svg');
const downloadIcon = apiService.getBlobAssetUrl('download.svg');
const Iconwrench = apiService.getBlobAssetUrl('icon-wrench.svg');
// const sendIcon = apiService.getBlobAssetUrl('send_12.svg'); // Commented out - Send action removed

const Surveys = () => {
  const [surveys, setSurveys] = useState([]);
  const [filteredSurveys, setFilteredSurveys] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showForm, setShowForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [surveyToDelete, setSurveyToDelete] = useState(null);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showSurveyTypeForm, setShowSurveyTypeForm] = useState(false);
  const [surveyTypes, setSurveyTypes] = useState([]);
  const [editData, setEditData] = useState(null);
  const [showDeleteSurveyTypeModal, setShowDeleteSurveyTypeModal] = useState(false);
  const [surveyTypeToDelete, setSurveyTypeToDelete] = useState(null);
  const [showColumnOptions, setShowColumnOptions] = useState(false);
  const [tableColumns, setTableColumns] = useState([]);
  // const [showSendSurveyPopup, setShowSendSurveyPopup] = useState(false); // Removed
  // const [selectedSurveyForSend, setSelectedSurveyForSend] = useState(null); // Removed

  // Commented out - Stats cards are hidden
  // const [stats, setStats] = useState({
  //   SentCount: 0,
  //   ResponseCount: 0,
  //   AvgResponseRate: 0,
  //   MostSentSurvey: "",
  // });

  // Define moduleAccess and hasWriteAccess before use
  const moduleAccess = JSON.parse(sessionStorage.getItem('module_access') || '{}');
  const hasWriteAccess = moduleAccess.Surveys === 'Read/Write';
  const userId = sessionStorage.getItem('user_id');

  // Initialize tableColumns after hasWriteAccess is defined
  useEffect(() => {
    setTableColumns([
      { key: 'name', label: 'Name', visible: true },
      { key: 'type', label: 'Type', visible: true },
      { key: 'active', label: 'Active', visible: true },
      { key: 'responseCount', label: 'Response Count', visible: true },
      { key: 'createdBy', label: 'Created By', visible: true },
      { key: 'createdOn', label: 'Created On', visible: true },
      { key: 'startDate', label: 'Start Date', visible: true },
      { key: 'endDate', label: 'End Date', visible: true },
      { key: 'actions', label: 'Actions', visible: hasWriteAccess },
    ]);
  }, [hasWriteAccess]);

  const refreshSurveys = async () => {
    setLoading(true);
    try {
      const data = await apiService.fetchData('surveys');
      const results = data?.results || [];
      const formatted = results.map((item) => ({
        id: item.SurveyID,
        name: item.Name,
        type: item.Type,
        active: item.Active,
        responseCount: item.ResponseCount,
        createdBy: item.CreatedBy,
        createdOn: item.CreatedOn,
        startDate: item.StartDate,
        endDate: item.EndDate,
      }));
      setSurveys(formatted);
    } catch (error) {
      console.error('Error fetching surveys:', error);
      setSnackbarMessage('Failed to fetch survey templates.');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchSurveyTypes = async () => {
    try {
      const surveyTypesData = await apiService.fetchData('surveys_types');
      setSurveyTypes(Array.isArray(surveyTypesData) ? surveyTypesData : surveyTypesData.results || []);
    } catch (error) {
      console.error('Error fetching survey types:', error);
      setSnackbarMessage('Failed to fetch survey types.');
      setSnackbarOpen(true);
    }
  };

  // Commented out - Stats cards are hidden
  // const fetchSurveyStatistics = async () => {
  //   try {
  //     const statsData = await apiService.fetchData('surveystatistics');
  //     setStats(statsData || {});
  //   } catch (error) {
  //     console.error('Error fetching survey statistics:', error);
  //     setSnackbarMessage('Failed to fetch survey statistics.');
  //     setSnackbarOpen(true);
  //   }
  // };

  useEffect(() => {
    fetchSurveyTypes();
  }, []);

  useEffect(() => {
    refreshSurveys();
  }, []);

  // useEffect(() => {
  //   fetchSurveyStatistics();
  // }, []); // Removed

  useEffect(() => {
    let filtered = surveys;

    if (filterType) {
      filtered = filtered.filter((s) => s.type === filterType);
    }

    if (searchTerm) {
      filtered = filtered.filter((s) =>
        Object.values(s).join(' ').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredSurveys(filtered);
    setCurrentPage(1);
  }, [searchTerm, filterType, surveys]);

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentSurveys = filteredSurveys.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredSurveys.length / itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEditClick = (survey) => {
    setSelectedSurvey(survey);
    setShowEditForm(true);
  };

  const handleDeleteClick = (survey) => {
    setSurveyToDelete(survey);
    setShowDeleteModal(true);
  };

  // Removed entirely
  // const handleSendClick = (survey) => {
  //   setSelectedSurveyForSend(survey);
  //   setShowSendSurveyPopup(true);
  // };

  const handleDeleteSurveyType = async () => {
    if (!surveyTypeToDelete) return;

    try {
      const token = process.env.REACT_APP_ACCESS_TOKEN;
      const payload = { surveytype_id: surveyTypeToDelete.ID || surveyTypeToDelete.surveytype_id };
      await apiService.deleteData('surveys_types', payload, token);
      const updatedSurveyTypes = surveyTypes.filter((t) => t.ID !== surveyTypeToDelete.ID);
      setSurveyTypes(updatedSurveyTypes);
      setSnackbarMessage('Survey type deleted successfully!');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error deleting survey type:', error);
      setSnackbarMessage('Failed to delete survey type.');
      setSnackbarOpen(true);
    } finally {
      setShowDeleteSurveyTypeModal(false);
      setSurveyTypeToDelete(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!surveyToDelete) return;

    try {
      const token = process.env.REACT_APP_ACCESS_TOKEN;
      await apiService.deleteData('surveys', { ID: surveyToDelete.id }, token);
      const updatedSurveys = surveys.filter((s) => s.id !== surveyToDelete.id);
      setSurveys(updatedSurveys);
      setShowDeleteModal(false);
      setSurveyToDelete(null);
      setSnackbarMessage('Survey deleted successfully!');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error deleting survey:', error);
      setSnackbarMessage('Failed to delete survey.');
      setSnackbarOpen(true);
    }
  };

  const exportToCSV = () => {
    const headers = tableColumns
      .filter((col) => col.visible && col.key !== 'actions')
      .map((col) => col.label)
      .join(',');

    const rows = surveys.map((survey) =>
      tableColumns
        .filter((col) => col.visible && col.key !== 'actions')
        .map((col) => {
          let value = survey[col.key];
          if (col.key === 'active') value = survey.active ? 'Yes' : 'No';
          return `"${value != null ? value.toString().replace(/"/g, '""') : ''}"`;
        })
        .join(',')
    );

    const csvContent = `data:text/csv;charset=utf-8,${headers}\n${rows.join('\n')}`;
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'survey_templates.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setSnackbarMessage('Exported to CSV successfully.');
    setSnackbarOpen(true);
  };

  const exportToPDF = () => {
    try {
      const doc = new jsPDF();
      const tableColumn = tableColumns
        .filter((col) => col.visible && col.key !== 'actions')
        .map((col) => col.label);
      const tableRows = filteredSurveys.map((survey) =>
        tableColumns
          .filter((col) => col.visible && col.key !== 'actions')
          .map((col) => {
            let value = survey[col.key];
            if (col.key === 'active') value = survey.active ? 'Yes' : 'No';
            return value != null ? value.toString() : '';
          })
      );

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 20,
        theme: 'striped',
        styles: { fontSize: 10 },
        headStyles: { fillColor: [63, 81, 181] },
      });

      doc.save('survey_templates.pdf');
      setSnackbarMessage('Exported to PDF successfully.');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      setSnackbarMessage('Failed to export to PDF.');
      setSnackbarOpen(true);
    }
  };

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: 175,
        width: '291px',
      },
    },
  };

  const handleExportClick = () => {
    setShowExportDropdown((prev) => !prev);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          backgroundColor: '#ffffff',
          color: '#333333',
        }}
      >
        <Box sx={{ width: '50%', maxWidth: '300px', position: 'relative' }}>
          <LinearProgress
            className="survey-tech-loading-bar"
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(1.62deg, #034589 -6.8%, #0066D1 107.85%);',
              },
            }}
          />
          <Box className="survey-circuit-overlay" />
        </Box>
        <Typography
          sx={{
            mt: 2,
            fontFamily: "'Mulish', sans-serif",
            fontSize: '1.2rem',
            fontWeight: 500,
            background: 'linear-gradient(1.62deg, #034589 -6.8%, #0066D1 107.85%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 0 4px rgba(0, 102, 209, 0.2)',
          }}
        >
          Loading Survey Templates...
        </Typography>
      </Box>
    );
  }

  return (
    <div>
      <Box sx={{ p: 3 }}>
        <div className="surveys-page">
          {/* Header row - Changed to Survey Templates */}
          <div className="add-survey-survey-header">
            <h1 className="add-survey-survey-title" id="survey-1" title="Survey Templates">
              Survey Templates
            </h1>
            {hasWriteAccess && (
              <div id="survey-15" className="add-survey-button-container">
                <img src={addicon} alt="addicon" className="logo-img" id="survey-3" />
                <button onClick={() => setShowForm(true)} id="survey-2" title="Add New Survey Template">
                  Add New Template
                </button>
              </div>
            )}
          </div>

          {/* Four cards row - Fully commented out */}
          {/*
          <div className="survey-cards-container">
            <div className="survey-card green">
              <h3>Total Surveys Sent</h3>
              <p>{stats.SentCount}</p>
            </div>
            <div className="survey-card orange">
              <h3>Total Responses Received</h3>
              <p>{stats.ResponseCount}</p>
            </div>
            <div className="survey-card blue">
              <h3>Average Response Rate</h3>
              <p>{stats.AvgResponseRate}%</p>
            </div>
            <div className="survey-card purple">
              <h3>Highest Responded Survey</h3>
              <p>{stats.MostSentSurvey}</p>
            </div>
          </div>
          */}

          <div id="survey-8">
            <Select
              value={surveyTypes.some((t) => t.Type === filterType) ? filterType : ''}
              onChange={(e) => setFilterType(e.target.value)}
              displayEmpty
              size="small"
              sx={{ width: 291 }}
              MenuProps={MenuProps}
              renderValue={(selected) => {
                if (!selected) {
                  return <span>All Survey Types</span>;
                }
                return selected;
              }}
            >
              <MenuItem id="type2" value="" title="All Survey Types">
                All Survey Types
              </MenuItem>
              {surveyTypes.map((type) => (
                <MenuItem key={type.Type} value={type.Type}>
                  <div id="type1" style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <span title={type.Type}>{type.Type}</span>
                    <div id="table-151">
                      <button
                        className="text-red-600 hover:text-red-800"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSurveyTypeToDelete(type);
                          setShowDeleteSurveyTypeModal(true);
                        }}
                        title="Delete"
                        id="table-5"
                      >
                        <img src={deleteteams} alt="delete" className="delete-img1" title="delete" />
                      </button>
                      <button
                        className="text-blue-600 hover:text-blue-800 ml-2"
                        onClick={(e) => {
                          const editData = {
                            surveytype_id: type.ID,
                            surveytype_name: type.Type,
                            surveytype_description: type.Description || '',
                          };
                          setEditData(editData);
                          setShowSurveyTypeForm(true);
                        }}
                        title="Edit"
                        id="table-5"
                      >
                        <img src={editIcon} alt="edit" id="delete-img2" className="delete-img1" title="edit" />
                      </button>
                    </div>
                  </div>
                </MenuItem>
              ))}
              <MenuItem
                onClick={() => {
                  setEditData(null);
                  setShowSurveyTypeForm(true);
                }}
                id="survey-2"
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 1,
                  backgroundColor: 'white !important',
                  paddingTop: 1,
                  paddingBottom: 1,
                }}
              >
                <img src={addicon} alt="addicon" className="logo-img" id="survey-3" title="addicon" />
                <span title="Add New Survey Type">Add New Survey Type</span>
              </MenuItem>
            </Select>
            <div className="survey-search-wrapper">
            <button
              style={{ width: '250px', height: '40px' }}
              className="survey-column-options-button"
              onClick={() => setShowColumnOptions(true)}
            >
              Column Options
              <img src={Iconwrench} alt="Wrench" className="Project-column-options-icon" />
            </button>
            <div className="search-input-container">
              <input
                style={{ height: '20px', width: '250px', paddingRight: '30px' }}
                type="text"
                className="survey-search"
                placeholder="Search here..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                title="Search here..."
              />
              <img
                src={search}
                alt="Search"
                className="search-img"
                onClick={(e) => {
                  e.stopPropagation();
                }}
                style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
              />
            </div>
          </div>
          </div>
          <div id="survey-16">
            <div className="survey-export-container">
              <img
                src={downloadIcon}
                alt="Export"
                className="downloadIcon-img"
                onClick={handleExportClick}
                style={{ cursor: 'pointer' }}
              />
              {showExportDropdown && (
                <div className="survey-export-dropdown">
                  <div className="survey-export-option" onClick={exportToCSV}>
                    Download CSV
                  </div>
                  <div className="survey-export-option" onClick={exportToPDF}>
                    Download PDF
                  </div>
                </div>
              )}
            </div>
            <Box>
              <Typography id="resultssurvey">
                Results: {filteredSurveys.length === 0 ? 0 : indexOfFirst + 1}-{Math.min(indexOfLast, filteredSurveys.length)} of {filteredSurveys.length}
              </Typography>
            </Box>
            <select
              className="survey-items-per-page-select"
              id="items-per-page-select-survey"
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
            >
              {[5, 10, 15, 20].map((count) => (
                <option key={count} value={count}>{count}</option>
              ))}
            </select>
          </div>
          <br />

          <SurveysTable
            surveys={currentSurveys}
            searchQuery={searchTerm}
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteClick}
            columns={tableColumns}
          />

          <div className="survey-pagination-container">
            <button
              className="survey-pagination-button"
              onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Previous Page"
            >
              &lt;
            </button>
            {(() => {
              const pageButtons = [];
              let startPage = Math.max(1, currentPage - 1);
              let endPage = Math.min(totalPages, startPage + 2);
              if (endPage - startPage < 2) {
                startPage = Math.max(1, endPage - 2);
              }
              for (let page = startPage; page <= endPage; page++) {
                pageButtons.push(
                  <button
                    key={`page-${page}`}
                    className={`survey-pagination-button ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageChange(page)}
                    aria-label={`Go to page ${page}`}
                  >
                    {page}
                  </button>
                );
              }
              return pageButtons;
            })()}
            <button
              className="survey-pagination-button"
              onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              aria-label="Next Page"
            >
              &gt;
            </button>
          </div>
        </div>

        {showSurveyTypeForm && (
          <div className="add-survey-modal-overlay">
            <AddSurveyTypeForm
              editData={editData}
              onClose={() => setShowSurveyTypeForm(false)}
              onSave={() => {
                refreshSurveys();
                fetchSurveyTypes();
              }}
              setSnackbarOpen={setSnackbarOpen}
              setSnackbarMessage={setSnackbarMessage}
              setFilterType={setFilterType}
            />
          </div>
        )}

        {showForm && (
          <div className="add-survey-modal-overlay">
            <AddSurveyForm
              onClose={() => setShowForm(false)}
              onSave={() => {
                setShowForm(false);
                refreshSurveys();
              }}
              key={editData?.surveytype_id || 'new'}
              setSnackbarOpen={setSnackbarOpen}
              setSnackbarMessage={setSnackbarMessage}
            />
          </div>
        )}
        {showEditForm && (
          <div className="add-survey-modal-overlay">
            <EditSurveyDetails
              surveyId={selectedSurvey?.id}
              onClose={() => {
                setShowEditForm(false);
                setSelectedSurvey(null);
              }}
              onSave={() => {
                setShowEditForm(false);
                setSelectedSurvey(null);
                refreshSurveys();
              }}
              setSnackbarOpen={setSnackbarOpen}
              setSnackbarMessage={setSnackbarMessage}
              snackbarOpen={snackbarOpen}
            />
          </div>
        )}
        {showDeleteModal && surveyToDelete && (
          <DeleteConfirmationModal
            surveyName={surveyToDelete.name}
            onCancel={() => setShowDeleteModal(false)}
            onConfirm={handleConfirmDelete}
            setSnackbarOpen={setSnackbarOpen}
            setSnackbarMessage={setSnackbarMessage}
          />
        )}
        {showDeleteSurveyTypeModal && surveyTypeToDelete && (
          <DeleteConfirmationModal
            surveyName={surveyTypeToDelete.Type}
            onCancel={() => {
              setShowDeleteSurveyTypeModal(false);
              setSurveyTypeToDelete(null);
            }}
            onConfirm={handleDeleteSurveyType}
            setSnackbarOpen={setSnackbarOpen}
            setSnackbarMessage={setSnackbarMessage}
          />
        )}
        {/* Removed SendSurveyPopup */}
        {/* {showSendSurveyPopup && selectedSurveyForSend && (
          <div className="add-survey-modal-overlay">
            <SendSurveyPopup ... />
          </div>
        )} */}

        {showColumnOptions && (
          <ColumnOptions
            columns={tableColumns}
            onSave={(updatedColumns) => {
              if (updatedColumns) {
                setTableColumns(updatedColumns);
                setSnackbarMessage('Column visibility updated successfully.');
                setSnackbarOpen(true);
              }
              setShowColumnOptions(false);
            }}
          />
        )}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
          anchorOrigin={
            snackbarMessage.includes('successful') 
              ? { vertical: 'bottom', horizontal: 'left' }
              : { vertical: 'top', horizontal: 'center' }
          }
          sx={{
            '& .MuiSnackbarContent-root': {
              backgroundColor: snackbarMessage.includes('successful') ? '#4caf50' : '#ff9800',
            },
            zIndex: 1400,
          }}
        />
      </Box>
    </div>
  );
};

export default Surveys;